import React, { useState, useEffect } from 'react';
import { X, Users, Check, Printer } from 'lucide-react';
import { logger } from '../utils/logger';

/**
 * StaffPrintModal Component
 * Modal for selecting which staff members to include in the printed directory
 */
const StaffPrintModal = ({
  isOpen,
  onClose,
  staffMembers,
  onPrint
}) => {
  const [selectedStaffIds, setSelectedStaffIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);

  // Initialize with all staff selected
  useEffect(() => {
    if (isOpen && staffMembers.length > 0) {
      setSelectedStaffIds(new Set(staffMembers.map(s => s.id)));
      setSelectAll(true);
    }
  }, [isOpen, staffMembers]);

  const handleToggleStaff = (staffId) => {
    setSelectedStaffIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(staffId)) {
        newSet.delete(staffId);
      } else {
        newSet.add(staffId);
      }
      setSelectAll(newSet.size === staffMembers.length);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedStaffIds(new Set());
      setSelectAll(false);
    } else {
      // Select all
      setSelectedStaffIds(new Set(staffMembers.map(s => s.id)));
      setSelectAll(true);
    }
  };

  const handlePrint = () => {
    const selectedStaff = staffMembers.filter(s => selectedStaffIds.has(s.id));
    logger.debug('Printing staff directory', {
      total: staffMembers.length,
      selected: selectedStaff.length
    });
    onPrint(selectedStaff);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Users className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">Print Staff Directory</h2>
              <p className="text-sm text-gray-600 mt-1">
                Select staff members to include in the printed directory
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Select All Toggle */}
          <div className="mb-4 pb-4 border-b border-gray-200">
            <button
              onClick={handleSelectAll}
              className={`
                w-full flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all
                ${selectAll
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center space-x-3">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  ${selectAll
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {selectAll ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Users className="w-5 h-5" />
                  )}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-800">
                    {selectAll ? 'Deselect All' : 'Select All'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {staffMembers.length} staff member{staffMembers.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* Staff List */}
          <div className="space-y-3">
            {staffMembers.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">No staff members found</p>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Staff Members ({selectedStaffIds.size} of {staffMembers.length} selected)
                </p>
                {staffMembers.map((staff) => (
                  <div
                    key={staff.id}
                    onClick={() => handleToggleStaff(staff.id)}
                    className={`
                      flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${selectedStaffIds.has(staff.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        ${selectedStaffIds.has(staff.id)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                        }
                      `}>
                        {selectedStaffIds.has(staff.id) ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Users className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{staff.name}</p>
                        <p className="text-sm text-gray-600">{staff.role || 'Team Member'}</p>
                        {staff.email && (
                          <p className="text-xs text-gray-500 mt-1">{staff.email}</p>
                        )}
                      </div>
                    </div>

                    {selectedStaffIds.has(staff.id) && (
                      <div className="flex items-center space-x-2 text-blue-600">
                        <Check className="w-5 h-5" />
                        <span className="text-sm font-medium">Selected</span>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            <strong>{selectedStaffIds.size}</strong> of {staffMembers.length} staff member{staffMembers.length !== 1 ? 's' : ''} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePrint}
              disabled={selectedStaffIds.size === 0}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print Directory</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffPrintModal;
